import React from 'react'
import * as R from 'remeda'

import {
  type CustomerAttribute,
  useSurveyQueryCustomerAttributeGroups,
  useSurveyQueryFlattenCustomerAttributes,
} from '~/hooks/useSurveyQuery'
import { differenceBy, exists } from '~/utils'

/**
 * ある顧客属性を直接または間接的に参照している顧客属性のIDを取得します。
 *
 * @param customerAttribute - 顧客属性。
 * @param customerAttributes - 顧客属性の配列。
 * @returns 参照している顧客属性のIDの配列。
 */
const getReferencedCustomerAttributeIds = (
  customerAttribute: CustomerAttribute,
  customerAttributes: CustomerAttribute[],
): string[] => {
  // 直接参照している顧客属性を取得します。
  const directReferencedCustomerAttributes = customerAttributes.filter(
    ({ filterGroups }) =>
      filterGroups.some((filterGroup) =>
        filterGroup.some((filter) =>
          filter.customerAttribute?.customerAttributeIds.includes(
            customerAttribute?.id,
          ),
        ),
      ),
  )
  // 直接参照している顧客属性のIDを取得します。
  const directReferencedCustomerAttributeIds =
    directReferencedCustomerAttributes.map(R.prop('id'))

  // 間接参照している顧客属性のIDを取得します。
  const indirectReferencedCustomerAttributeIds =
    directReferencedCustomerAttributes.flatMap((customerAttribute) =>
      getReferencedCustomerAttributeIds(customerAttribute, customerAttributes),
    )

  // 直接参照している顧客属性のIDと間接参照している顧客属性のIDを結合します。
  return R.pipe(
    R.concat(
      directReferencedCustomerAttributeIds,
      indirectReferencedCustomerAttributeIds,
    ),
    R.unique(),
  )
}

export const useAvailableCustomerAttributeGroups = (
  customerAttribute: CustomerAttribute | null | undefined,
) => {
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const allCustomerAttributes = useSurveyQueryFlattenCustomerAttributes()

  const selfOrReferencedCustomerAttributeIds = React.useMemo(
    () =>
      exists(customerAttribute)
        ? [
            customerAttribute.id,
            ...getReferencedCustomerAttributeIds(
              customerAttribute,
              allCustomerAttributes,
            ),
          ]
        : [],
    [allCustomerAttributes, customerAttribute],
  )

  return React.useMemo(
    () =>
      R.pipe(
        customerAttributeGroups,
        R.map(({ customerAttributes, ...rest }) => ({
          ...rest,
          customerAttributes: differenceBy(
            customerAttributes,
            selfOrReferencedCustomerAttributeIds,
            R.prop('id'),
            R.identity(),
          ),
        })),
        R.filter(({ customerAttributes }) => 0 < customerAttributes.length),
      ),
    [customerAttributeGroups, selfOrReferencedCustomerAttributeIds],
  )
}
